import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CanvasComponent, ComponentType, EasyCodeAction, ProjectSettings } from "@/types";
import {
  Bold,
  Italic,
  Underline,
  Lock,
  Unlock,
  Type,
  Trash2,
  SendToBack,
  BringToFront,
  Forward as BringForward,
  Palette,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Square,
  CircleSlash,
  Code,
  Eye,
  EyeOff,
  MessageSquare,
  Tag,
  FolderTree,
  FolderInput,
  Layers,
} from "lucide-react";
import { useState } from "react";

interface ComponentContextMenuProps {
  component: CanvasComponent;
  children: React.ReactNode;
  onUpdate: (component: CanvasComponent) => void;
  onDelete: (id: string) => void;
  onZIndexChange: (id: string, action: 'front' | 'forward' | 'backward' | 'back') => void;
  components: CanvasComponent[];
  settings: ProjectSettings;
}

export default function ComponentContextMenu({
  component,
  children,
  onUpdate,
  onDelete,
  onZIndexChange,
  components,
  settings,
}: ComponentContextMenuProps) {
  const [showEasyCodeDialog, setShowEasyCodeDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<'onClick' | 'onHover'>('onClick');
  const [selectedAction, setSelectedAction] = useState<'setValue' | 'setLabel' | 'setVisibility' | 'luaAction'>('setValue');
  const [selectedTargetIds, setSelectedTargetIds] = useState<string[]>([]);
  const [actionValue, setActionValue] = useState('');
  const [luaAction, setLuaAction] = useState('');
  const [luaData, setLuaData] = useState('{}');
  const [selectedDataSources, setSelectedDataSources] = useState<Array<{
    key: string;
    componentId: string;
  }>>([]);

  const handleOpacityChange = (value: number[]) => {
    onUpdate({
      ...component,
      properties: {
        ...component.properties,
        opacity: value[0],
      },
    });
  };

  const handleStyleChange = (property: keyof ComponentStyles, value: any) => {
    onUpdate({
      ...component,
      properties: {
        ...component.properties,
        styles: {
          ...component.properties.styles,
          [property]: value,
        },
      },
    });
  };

  const handleFontStyleChange = (style: keyof FontStyles, value: boolean) => {
    onUpdate({
      ...component,
      properties: {
        ...component.properties,
        styles: {
          ...component.properties.styles,
          fontStyles: {
            ...component.properties.styles?.fontStyles,
            [style]: value,
          },
        },
      },
    });
  };

  const handleLockChange = () => {
    onUpdate({
      ...component,
      properties: {
        ...component.properties,
        isLocked: !component.properties.isLocked,
      },
    });
  };

  const generateEasyCode = () => {
    let code = '';
    const eventType = selectedEvent.toLowerCase().slice(2);
    
    switch (selectedAction) {
      case 'setValue':
        code = selectedTargetIds.map(id => 
          `document.getElementById('${id}').value = '${actionValue}';`
        ).join('\n');
        break;
      case 'setLabel':
        code = selectedTargetIds.map(id => 
          `document.getElementById('${id}').textContent = '${actionValue}';`
        ).join('\n');
        break;
      case 'setVisibility':
        if (actionValue === 'toggle') {
          code = selectedTargetIds.map(id => `
const element_${id} = document.getElementById('${id}');
element_${id}.style.display = element_${id}.style.display === 'none' ? 'block' : 'none';`
          ).join('\n');
        } else {
          code = selectedTargetIds.map(id => 
            `document.getElementById('${id}').style.display = '${actionValue === 'true' ? 'block' : 'none'}';`
          ).join('\n');
        }
        break;
      case 'luaAction':
        const dataAssignments = selectedDataSources.map(source => {
          const component = components.find(c => c.properties.name === source.componentId);
          return `  "${source.key}": document.getElementById('${source.componentId}').${component?.type === 'input' ? 'value' : 'textContent'}`;
        });
        
        const dynamicData = dataAssignments.length > 0 
          ? `{\n${dataAssignments.join(',\n')}\n}`
          : luaData;

        code = `fetch('https://${settings.nickname}/${luaAction}', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json'
  },
  body: JSON.stringify(${dynamicData})
});`;
        break;
    }

    onUpdate({
      ...component,
      events: {
        ...component.events,
        [selectedEvent]: code,
      },
    });

    setShowEasyCodeDialog(false);
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent className="w-64">
        <ContextMenuItem
          onClick={() => onZIndexChange(component.id, 'front')}
          className="gap-2"
        >
          <BringToFront className="w-4 h-4" /> Bring to Front
        </ContextMenuItem>
        <ContextMenuItem
          onClick={() => onZIndexChange(component.id, 'forward')}
          className="gap-2"
        >
          <BringForward className="w-4 h-4" /> Bring Forward
        </ContextMenuItem>
        <ContextMenuItem
          onClick={() => onZIndexChange(component.id, 'backward')}
          className="gap-2"
        >
          <SendToBack className="w-4 h-4" /> Send Backward
        </ContextMenuItem>
        <ContextMenuItem
          onClick={() => onZIndexChange(component.id, 'back')}
          className="gap-2"
        >
          <SendToBack className="w-4 h-4" /> Send to Back
        </ContextMenuItem>

        <ContextMenuSeparator />

        <div className="px-2 py-1.5 text-sm">
          Opacity
          <Slider
            defaultValue={[component.properties.opacity ?? 100]}
            max={100}
            step={1}
            className="my-2"
            onValueChange={handleOpacityChange}
          />
        </div>

        <ContextMenuSeparator />

        <ContextMenuSub>
          <ContextMenuSubTrigger className="gap-2">
            <Palette className="w-4 h-4" /> Styles
          </ContextMenuSubTrigger>
          <ContextMenuSubContent className="w-64">
            <div className="p-2 space-y-2">
              <div>
                <Label>Background Color</Label>
                <Input
                  type="color"
                  value={component.properties.styles?.backgroundColor ?? "#ffffff"}
                  onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
                  className="h-8"
                />
              </div>
              
              <div>
                <Label>Border Color</Label>
                <Input
                  type="color"
                  value={component.properties.styles?.borderColor ?? "#000000"}
                  onChange={(e) => handleStyleChange('borderColor', e.target.value)}
                  className="h-8"
                />
              </div>

              <div>
                <Label>Border Width</Label>
                <Slider
                  defaultValue={[component.properties.styles?.borderWidth ?? 0]}
                  min={0}
                  max={10}
                  step={1}
                  onValueChange={(value) => handleStyleChange('borderWidth', value[0])}
                />
              </div>

              <div>
                <Label>Border Radius</Label>
                <Slider
                  defaultValue={[component.properties.styles?.borderRadius ?? 0]}
                  min={0}
                  max={50}
                  step={1}
                  onValueChange={(value) => handleStyleChange('borderRadius', value[0])}
                />
              </div>

              <div>
                <Label>Padding</Label>
                <Slider
                  defaultValue={[component.properties.styles?.padding ?? 0]}
                  min={0}
                  max={50}
                  step={1}
                  onValueChange={(value) => handleStyleChange('padding', value[0])}
                />
              </div>

              <div>
                <Label>Text Align</Label>
                <Select
                  value={component.properties.styles?.textAlign ?? "left"}
                  onValueChange={(value) => handleStyleChange('textAlign', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left">
                      <div className="flex items-center">
                        <AlignLeft className="w-4 h-4 mr-2" /> Left
                      </div>
                    </SelectItem>
                    <SelectItem value="center">
                      <div className="flex items-center">
                        <AlignCenter className="w-4 h-4 mr-2" /> Center
                      </div>
                    </SelectItem>
                    <SelectItem value="right">
                      <div className="flex items-center">
                        <AlignRight className="w-4 h-4 mr-2" /> Right
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Font Size</Label>
                <Slider
                  defaultValue={[component.properties.styles?.fontSize ?? 16]}
                  min={8}
                  max={72}
                  step={1}
                  onValueChange={(value) => handleStyleChange('fontSize', value[0])}
                />
              </div>

              <div>
                <Label>Font Color</Label>
                <Input
                  type="color"
                  value={component.properties.styles?.fontColor ?? "#000000"}
                  onChange={(e) => handleStyleChange('fontColor', e.target.value)}
                  className="h-8"
                />
              </div>

              <div className="flex gap-2">
                <button
                  className={`p-2 rounded ${
                    component.properties.styles?.fontStyles?.bold
                      ? "bg-accent"
                      : "hover:bg-accent/50"
                  }`}
                  onClick={() =>
                    handleFontStyleChange(
                      "bold",
                      !component.properties.styles?.fontStyles?.bold
                    )
                  }
                >
                  <Bold className="w-4 h-4" />
                </button>
                <button
                  className={`p-2 rounded ${
                    component.properties.styles?.fontStyles?.italic
                      ? "bg-accent"
                      : "hover:bg-accent/50"
                  }`}
                  onClick={() =>
                    handleFontStyleChange(
                      "italic",
                      !component.properties.styles?.fontStyles?.italic
                    )
                  }
                >
                  <Italic className="w-4 h-4" />
                </button>
                <button
                  className={`p-2 rounded ${
                    component.properties.styles?.fontStyles?.underline
                      ? "bg-accent"
                      : "hover:bg-accent/50"
                  }`}
                  onClick={() =>
                    handleFontStyleChange(
                      "underline",
                      !component.properties.styles?.fontStyles?.underline
                    )
                  }
                >
                  <Underline className="w-4 h-4" />
                </button>
              </div>
            </div>
          </ContextMenuSubContent>
        </ContextMenuSub>

        <ContextMenuSeparator />

        <ContextMenuItem 
          onClick={() => {
            setSelectedTargetIds([]);
            setActionValue('');
            setLuaAction('');
            setLuaData('{}');
            setShowEasyCodeDialog(true);
          }} 
          className="gap-2"
        >
          <Code className="w-4 h-4" /> Easy Code
        </ContextMenuItem>

        <ContextMenuSeparator />

        <ContextMenuItem onClick={handleLockChange} className="gap-2">
          {component.properties.isLocked ? (
            <>
              <Unlock className="w-4 h-4" /> Unlock Component
            </>
          ) : (
            <>
              <Lock className="w-4 h-4" /> Lock Component
            </>
          )}
        </ContextMenuItem>

        <ContextMenuSeparator />

        <ContextMenuSub>
          <ContextMenuSubTrigger className="gap-2">
            <FolderTree className="w-4 h-4" /> Grouping
          </ContextMenuSubTrigger>
          <ContextMenuSubContent>
            {component.properties.isContainer && (
              <ContextMenuItem
                onClick={() => {
                  onUpdate({
                    ...component,
                    properties: {
                      ...component.properties,
                      isContainer: true,
                      scrollable: true,
                    },
                  });
                }}
                className="gap-2"
              >
                <Layers className="w-4 h-4" /> Make Container
              </ContextMenuItem>
            )}
            <ContextMenuItem
              onClick={() => {
                // Create a new panel component as parent
                const newPanel: CanvasComponent = {
                  id: `panel-${Date.now()}`,
                  type: ComponentType.PANEL,
                  position: { ...component.position },
                  size: { 
                    width: component.size.width + 40, 
                    height: component.size.height + 40 
                  },
                  properties: {
                    text: 'New Group',
                    name: `Group${components.length + 1}`,
                    isContainer: true,
                    scrollable: true,
                  },
                  events: {
                    onClick: '',
                    onHover: '',
                    onDrag: '',
                  },
                };

                // Update the component to be a child of the new panel
                const updatedComponent = {
                  ...component,
                  position: { x: 20, y: 20 },
                  properties: {
                    ...component.properties,
                    parentId: newPanel.id,
                  },
                };

                // Add both components
                onUpdate(newPanel);
                onUpdate(updatedComponent);
              }}
              className="gap-2"
            >
              <FolderInput className="w-4 h-4" /> Create New Group
            </ContextMenuItem>
          </ContextMenuSubContent>
        </ContextMenuSub>

        <ContextMenuSeparator />

        <ContextMenuItem
          onClick={() => onDelete(component.id)}
          className="gap-2 text-destructive focus:text-destructive"
        >
          <Trash2 className="w-4 h-4" /> Delete Component
        </ContextMenuItem>

        <Dialog open={showEasyCodeDialog} onOpenChange={setShowEasyCodeDialog}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Easy Code Generator</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Event Type</Label>
                <Select
                  value={selectedEvent}
                  onValueChange={(value: 'onClick' | 'onHover') => setSelectedEvent(value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="onClick">On Click</SelectItem>
                    <SelectItem value="onHover">On Hover</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label>Action Type</Label>
                <Select
                  value={selectedAction}
                  onValueChange={(value: 'setValue' | 'setLabel' | 'setVisibility' | 'luaAction') => setSelectedAction(value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="setValue">
                      <div className="flex items-center">
                        <Type className="w-4 h-4 mr-2" /> Set Value
                      </div>
                    </SelectItem>
                    <SelectItem value="setLabel">
                      <div className="flex items-center">
                        <Tag className="w-4 h-4 mr-2" /> Set Label
                      </div>
                    </SelectItem>
                    <SelectItem value="setVisibility">
                      <div className="flex items-center">
                        <Eye className="w-4 h-4 mr-2" /> Set Visibility
                      </div>
                    </SelectItem>
                    <SelectItem value="luaAction">
                      <div className="flex items-center">
                        <MessageSquare className="w-4 h-4 mr-2" /> Lua Action
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {selectedAction !== 'luaAction' && (
                <div className="grid gap-2">
                  <Label>Target Components</Label>
                  <div className="max-h-40 overflow-y-auto border rounded-md p-2">
                    {components.map((comp) => (
                      <div key={comp.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`target-${comp.id}`}
                          checked={selectedTargetIds.includes(comp.properties.name)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTargetIds([...selectedTargetIds, comp.properties.name]);
                            } else {
                              setSelectedTargetIds(selectedTargetIds.filter(id => id !== comp.properties.name));
                            }
                          }}
                          className="rounded border-gray-300"
                        />
                        <label htmlFor={`target-${comp.id}`}>
                          {comp.properties.name} ({comp.type})
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedAction === 'setVisibility' ? (
                <div className="grid gap-2">
                  <Label>Visibility</Label>
                  <Select
                    value={actionValue}
                    onValueChange={setActionValue}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">
                        <div className="flex items-center">
                          <Eye className="w-4 h-4 mr-2" /> Show
                        </div>
                      </SelectItem>
                      <SelectItem value="false">
                        <div className="flex items-center">
                          <EyeOff className="w-4 h-4 mr-2" /> Hide
                        </div>
                      </SelectItem>
                      <SelectItem value="toggle">
                        <div className="flex items-center">
                          <Eye className="w-4 h-4 mr-2" /> Toggle Visibility
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : selectedAction === 'luaAction' ? (
                <>
                  <div className="grid gap-2">
                    <Label>Lua Action Name</Label>
                    <Input
                      value={luaAction}
                      onChange={(e) => setLuaAction(e.target.value)}
                      placeholder="e.g., inventory/use"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Data Sources</Label>
                    <div className="space-y-2">
                      {selectedDataSources.map((source, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            value={source.key}
                            onChange={(e) => {
                              const newSources = [...selectedDataSources];
                              newSources[index].key = e.target.value;
                              setSelectedDataSources(newSources);
                            }}
                            placeholder="Data key"
                            className="flex-1"
                          />
                          <Select
                            value={source.componentId}
                            onValueChange={(value) => {
                              const newSources = [...selectedDataSources];
                              newSources[index].componentId = value;
                              setSelectedDataSources(newSources);
                            }}
                          >
                            <SelectTrigger className="w-[200px]">
                              <SelectValue placeholder="Select component" />
                            </SelectTrigger>
                            <SelectContent>
                              {components.map((comp) => (
                                <SelectItem key={comp.id} value={comp.properties.name}>
                                  {comp.properties.name} ({comp.type})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => {
                              const newSources = selectedDataSources.filter((_, i) => i !== index);
                              setSelectedDataSources(newSources);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        onClick={() => {
                          setSelectedDataSources([
                            ...selectedDataSources,
                            { key: '', componentId: '' }
                          ]);
                        }}
                      >
                        Add Data Source
                      </Button>
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label>Static Data (JSON)</Label>
                    <Input
                      value={luaData}
                      onChange={(e) => setLuaData(e.target.value)}
                      placeholder='e.g., {"itemId": "123"}'
                    />
                    <p className="text-sm text-muted-foreground">
                      Use static data when not using component values
                    </p>
                  </div>
                </>
              ) : (
                <div className="grid gap-2">
                  <Label>Value</Label>
                  <Input
                    value={actionValue}
                    onChange={(e) => setActionValue(e.target.value)}
                    placeholder="Enter value..."
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEasyCodeDialog(false)}>
                Cancel
              </Button>
              <Button onClick={generateEasyCode}>Generate Code</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </ContextMenuContent>
    </ContextMenu>
  );
}